import { createFileRoute } from "@tanstack/react-router";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { Check } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { ProductSheetButton } from "@/components/site/ProductSheetButton";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { FormatShowcase } from "@/components/site/FormatShowcase";
import { ContactForm } from "@/components/site/ContactForm";
import { PulseFAQ } from "@/components/site/PulseFAQ";
import { FitCheck } from "@/components/site/FitCheck";
import { PulseDeliveryBox } from "@/components/site/PulseDeliveryBox";
import { RiskRemoval } from "@/components/site/RiskRemoval";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { HeroVideoLoop } from "@/components/site/wow/HeroVideoLoop";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { RhythmTimeline } from "@/components/site/wow/RhythmTimeline";
import { RoiCalculator } from "@/components/site/hub/RoiCalculator";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";
import { videoObject, aeoGeo } from "@/lib/seo/schema";

const SITE_ORIGIN = "https://fenny.se";
const PULSE_URL = `${SITE_ORIGIN}/pulse`;
import { SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
const PULSE_OG_IMAGE = SOCIAL_IMAGE_URL;

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { q: "Vad ingår i ett Pulse-abonnemang?", a: "Manus, regi, inspelning, klipp, grading, ljudmix och formatanpassning för era kanaler — allt ingår i en månadskostnad. Ni får en fast projektledare och en jämn leveransrytm där vi planerar månadens innehåll tillsammans." },
    { q: "Hur lång är bindningstiden?", a: "Ingen bindning. Pulse rullar månadsvis — säg upp när ni vill utan uppsägningstid. Inga dolda avgifter, inga timdebiteringar." },
    { q: "Hur snabbt kommer ni igång?", a: "Från första mötet till första inspelning är det normalt 2–3 veckor. Vi gör en kort strategiworkshop, sätter en innehållsplan för första kvartalet och bokar in spelningsdagar direkt." },
    { q: "Vad kostar Pulse?", a: "Pulse börjar från 14 995 kr/mån (Pulse Lite). Pulse Core ligger på 34 995 kr/mån och Pulse Pro på 45 995 kr/mån. Exakt nivå beror på hur mycket innehåll ni behöver per månad och vilka format. Vi räknar fram ett konkret upplägg på vårt första möte — kostnadsfritt och utan förpliktelser." },
    { q: "Hur fungerar leveranserna i praktiken?", a: "Vi spelar in i block 1–2 gånger per månad och levererar löpande färdiga filmer i de format ni behöver: 16:9 för YouTube och webb, 9:16 för Reels/TikTok, 1:1 för LinkedIn. Allt i en delad mapp, redo att publicera." },
    { q: "Kan vi blanda Pulse med större Prime-produktioner?", a: "Absolut — många kunder gör det. Pulse håller takten löpande, medan Prime används för större kampanjer, brandfilmer eller event. Samma team, samma kvalitetsnivå." },
    { q: "Äger vi materialet?", a: "Ja. Allt slutmaterial är ert att använda fritt i era egna kanaler. Vi behåller rätten att visa utvalda klipp i vår portfolio om inget annat avtalas." },
    { q: "Var spelar ni in?", a: "Vi arbetar i hela Sverige och Norden. Många produktioner sker hos er, på location eller i vår studio. Resa och logistik är inkluderat inom Sverige för Pulse-abonnemang." },
  ].map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Fenny Pulse",
  serviceType: "Filmabonnemang för marknadsteam",
  provider: { "@type": "Organization", name: "Fenny", url: SITE_ORIGIN },
  areaServed: ["SE", "NO", "DK", "FI"],
  description:
    "Premium filmabonnemang. Manus, produktion, klipp, grading och formatanpassning i en löpande månadstjänst.",
  image: PULSE_OG_IMAGE,
  url: PULSE_URL,
  offers: {
    "@type": "Offer",
    priceCurrency: "SEK",
    price: "29000",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: "29000",
      priceCurrency: "SEK",
      referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
    },
    availability: "https://schema.org/InStock",
    url: PULSE_URL,
  },
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE_ORIGIN}/` },
    { "@type": "ListItem", position: 2, name: "Pulse", item: PULSE_URL },
  ],
};

export const Route = createFileRoute("/pulse")({
  head: () => ({
    meta: [
      { title: "Pulse — Film på abonnemang | Fenny" },
      {
        name: "description",
        content:
          "Fenny Pulse är ett filmabonnemang för marknadsteam som vill ha löpande, högkvalitativt videoinnehåll. Manus, produktion, klipp och formatanpassning i en tjänst.",
      },
      { property: "og:title", content: "Pulse — Film på abonnemang | Fenny" },
      {
        property: "og:description",
        content:
          "Filmabonnemang för marknadsteam. Manus, produktion, klipp och formatanpassning i en och samma tjänst.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/pulse" },
      { property: "og:image", content: PULSE_OG_IMAGE },
      { property: "og:image:alt", content: "Fenny Pulse — film på abonnemang" },
      { property: "og:image:width", content: "1024" },
      { property: "og:image:height", content: "576" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Pulse — Film på abonnemang | Fenny" },
      { name: "twitter:description", content: "Filmabonnemang för marknadsteam." },
      { name: "twitter:image", content: PULSE_OG_IMAGE },
      { name: "twitter:image:alt", content: "Fenny Pulse — film på abonnemang" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/pulse" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/pulse", name: "Pulse — Film på abonnemang | Fenny", description: "Fenny Pulse är ett filmabonnemang för marknadsteam som vill ha löpande, högkvalitativt videoinnehåll. Manus, produktion, klipp och formatanpassning i en tjänst."})) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      // FEN-015: VideoObject för featured-filmen — krävs för video-rich-results
      // och AI Overviews där Google läser thumbnail + embedUrl.
      {
        type: "application/ld+json",
        children: JSON.stringify(
          videoObject({
            name: "Garageportexperten Hemma — Pulse-leverans",
            description: "Löpande kampanjfilm producerad inom Fenny Pulse-abonnemanget för Garageportexperten.",
            thumbnailUrl: "https://i.ytimg.com/vi/O2zdqLfmM5E/hqdefault.jpg",
            uploadDate: "2024-09-01",
            embedUrl: "https://www.youtube-nocookie.com/embed/O2zdqLfmM5E",
            contentUrl: "https://www.youtube.com/watch?v=O2zdqLfmM5E",
          }),
        ),
      },
    ],
  }),

  component: PulsePage,
});


const heroImages = [
  "/prime-stills/pricka-fenny-1.jpg",
  "/prime-stills/also-fenny-1.jpg",
  "/prime-stills/wimeck-fenny-1.jpg",
];
const marqueeImages = [
  "/prime-stills/garageportexperten-fenny-1.jpg",
  "/prime-stills/also-fenny-1.jpg",
  "/prime-stills/pricka-fenny-1.jpg",
  "/prime-stills/wimeck-fenny-1.jpg",
  "/prime-stills/fenny-reklam-film-1.jpg",
  "/prime-stills/elektronikbranschen-fenny-1.jpg",
  "/prime-stills/garageportexperten-fenny-3.jpg",
  "/prime-stills/raaaabaatar-fenny-1.jpg",
  "/prime-stills/bolt-fenny-1.jpg",
  "/prime-stills/garageportexperten-fenny-6.jpg",
  "/prime-stills/recompany-1.jpg",
];

const values = [
  {
    title: "Från strategisk planering till löpande produktion",
    body: "Vi är en tillväxtpartner som tar ägandeskap för hela produktionen.",
  },
  {
    title: "Samma premiumkvalitet och team hela tiden",
    body: "Vi filmar med professionell utrustning och lägger stor vikt vid ljus, vinklar och ljud.",
  },
  {
    title: "Leveranser för de kanaler ni prioriterar",
    body: "Tillsammans sätter vi en plan för året, fastställer kanalval och producerar för de formaten.",
  },
  {
    title: "Transparent fast prissättning varje månad",
    body: "En förutsägbar prismodell som gör att ni kan fastställa budget till en lägre kostnad än tidigare.",
  },
];

const gallery = [
  { id: "GPm8ubB79DM", title: "Årets Pryl 2026" },
  { id: "HwWGVOl6dtg", title: "ALSO Huawei Energy Solution in Denmark" },
  { id: "yvojXrb41Aw", title: "Bolt kampanj (20 sek)" },
  
  { id: "KkE8BqgQ14M", title: "30 år med Garageportexperten" },
  { id: "WONrMkeJuik", title: "Wimeck Detailing — Allt på en plats" },
];

function PulsePage() {
  const marquee = [...marqueeImages, ...marqueeImages];
  useScrollDepth("pulse");

  return (
    <div>
      {/* Hero with cinematic crossfading background */}
      <section className="relative isolate overflow-hidden">
        <HeroVideoLoop images={heroImages} tone="mint" />

        <div className="mx-auto max-w-6xl px-6 pt-40 pb-24 md:pt-52 md:pb-32 lg:pt-60 lg:pb-40">
          <div className="max-w-3xl">
            <Reveal>
              <LivePulseBadge tone="mint" className="mb-6">
                Pulse · pågående månad
              </LivePulseBadge>
            </Reveal>
            <KineticHeading
              text="Film på abonnemang."
              className="text-5xl font-semibold leading-[1.02] text-butter md:text-7xl lg:text-[88px]"
              highlight={{ indices: [], className: "" }}
            />
            <QuickAnswer answerId="sv_hur_fungerar_fenny_pulse" tone="mint" />
            <Reveal delay={0.5}>
              <p className="mt-8 max-w-2xl text-pretty text-lg text-foreground/90 md:text-xl">
                Med Fenny Pulse får ni ett filmabonnemang där ni bestämmer innehållet — reklamfilm,
                SoMe, casefilmer och andra rörliga medier. Ni fokuserar på tillväxt, vi sköter
                content-maskineriet.
              </p>
            </Reveal>
            <Reveal delay={0.7}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Magnetic>
                  <Button to="/kontakt" variant="mint" arrow trackId="book_strategy_meeting" trackProps={{ location: "pulse_hero" }}>
                    Boka ett kostnadsfritt strategimöte
                  </Button>
                </Magnetic>
                <Button href="#funkar" variant="outline" trackId="how_pulse_works" trackProps={{ location: "pulse_hero" }}>
                  Så funkar Pulse
                </Button>
                <Button href="#faq" variant="ghost">
                  Vanliga frågor
                </Button>
              </div>
            </Reveal>

            {/* Live counter strip */}
            <Reveal delay={0.9}>
              <dl className="mt-14 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8">
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-mint">Levererat 2026</dt>
                  <dd className="mt-1 text-3xl font-semibold text-foreground md:text-4xl">
                    <LiveCountUp value={247} />
                  </dd>
                  <div className="text-xs text-muted-foreground">filmer</div>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-mint">Aktiva abonnemang</dt>
                  <dd className="mt-1 text-3xl font-semibold text-foreground md:text-4xl">
                    <LiveCountUp value={38} />
                  </dd>
                  <div className="text-xs text-muted-foreground">varumärken</div>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-mint">Längsta relation</dt>
                  <dd className="mt-1 text-3xl font-semibold text-foreground md:text-4xl">
                    <LiveCountUp value={4} suffix="år" />
                  </dd>
                  <div className="text-xs text-muted-foreground">och pågående</div>
                </div>
              </dl>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Image marquee */}
      <section
        aria-label="Pulse-produktioner"
        className="pulse-marquee-mask group/marquee relative overflow-hidden border-y border-white/10 py-5 md:py-6"
      >
        <div className="pulse-marquee flex w-max gap-3 md:gap-4" aria-hidden="true">
          {marquee.map((src, i) => (
            <div
              key={i}
              className="h-32 w-[220px] shrink-0 overflow-hidden rounded-xl bg-surface md:h-48 md:w-[360px] md:rounded-2xl lg:h-56 lg:w-[420px]"
            >
              <img
                src={src}
                alt={`Stillbild ur en Pulse-produktion av Fenny (bild ${(i % marquee.length) + 1})`}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          ))}
        </div>
        <style>{`
          @keyframes pulseMarquee {
            from { transform: translate3d(0,0,0); }
            to   { transform: translate3d(-50%,0,0); }
          }
          .pulse-marquee {
            animation: pulseMarquee var(--marquee-duration, 35s) linear infinite;
            will-change: transform;
            backface-visibility: hidden;
          }
          .group\\/marquee:hover .pulse-marquee { animation-play-state: paused; }
          .pulse-marquee-mask {
            -webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
                    mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
          }
          @media (min-width: 768px)  { .pulse-marquee { --marquee-duration: 55s; } }
          @media (min-width: 1280px) { .pulse-marquee { --marquee-duration: 75s; } }
          @media (prefers-reduced-motion: reduce) {
            .pulse-marquee { animation: none; transform: none; }
          }
        `}</style>
      </section>

      {/* Intro + value cards */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-[1.05fr_1.25fr] lg:gap-16">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">Pulse</div>
              <h2 className="text-balance text-3xl font-semibold leading-tight text-mint md:text-4xl lg:text-[44px]">
                Innehåll som håller takten med er tillväxt.
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
                <p>
                  Med Fenny Pulse slipper ni långsamma processer, överbelastade team och halvdana
                  ad-hoc-lösningar — och framför allt får ni en jämn kvalitetsnivå med nytt, relevant
                  och engagerande material där ni bestämmer vilken sorts material vi spelar in från
                  gång till gång.
                </p>
                <p>
                  Pulse är byggt för marknadschefer och marknadsteam som vill ha ett contentmaskineri
                  som snurrar utan handpåläggning — där manus, produktion, klippning, grading,
                  formatanpassning och planering redan är löst i en och samma tjänst.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button to="/priser" variant="mint" arrow>Se våra priser</Button>
                <ProductSheetButton location="pulse_intro" />
              </div>
            </Reveal>
          </div>

          <RevealStagger className="grid gap-4 sm:grid-cols-2">
            {values.map((v) => (
              <RevealItem key={v.title}>
                <TiltCard className="h-full rounded-3xl border border-white/10 bg-surface p-6 transition-colors hover:border-mint/40 md:p-7" glow="mint">
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-mint/15 text-mint ring-1 ring-mint/30">
                    <Check className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-base font-semibold leading-snug md:text-[17px]">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                </TiltCard>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </Section>

      {/* Concrete monthly delivery — what you actually get */}
      <PulseDeliveryBox />

      {/* ROI calculator */}
      <Section className="bg-surface/30">
        <EyebrowHeading
          eyebrow="Räkna baklänges"
          title="Hur mycket film behöver ni egentligen?"
          lede="Tre snabba val — vi pekar ut rätt Pulse-nivå med pris och takt."
        />
        <div className="mt-12">
          <RoiCalculator />
        </div>
      </Section>

      {/* Fit check */}
      <FitCheck
        eyebrow="Är Pulse rätt för er?"
        title="Vi vill inte ha kunder vi inte gör nytta för."
        fits={[
          "Ni behöver löpande rörligt innehåll varje månad till flera kanaler",
          "Ni vill äga ett innehållsflöde i stället för att jaga byråer per kampanj",
          "Ni värdesätter premiumkvalitet men har inte tid att detaljstyra produktion",
        ]}
        noFits={[
          "Ni behöver en enstaka kampanjfilm — se Prime i stället",
          "Ni vill ha lägsta möjliga pris snarare än premiumkvalitet i löpande flöde",
        ]}
        tone="mint"
      />

      {/* Risk removal — the four big worries */}
      <RiskRemoval />

      {/* Video gallery */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Exempel"
          title="Ett axplock av Pulse-leveranser"
          lede="Sex format. Sex kunder. Ett tempo som ni styr."
        />
        <RevealStagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {gallery.map((v, i) => (
            <RevealItem key={v.id}>
              <VideoEmbed id={v.id} title={v.title} priority={i < 3 ? "high" : "auto"} />
            </RevealItem>
          ))}
        </RevealStagger>
      </Section>

      {/* Rhythm timeline (replaces flat process grid) */}
      <Section id="funkar">
        <EyebrowHeading
          eyebrow="Så funkar Pulse"
          title="En förutsägbar rytm — vecka för vecka."
          lede="Pulse är inte ad-hoc. Varje månad följer samma ramverk så ni alltid vet vad som kommer."
        />
        <div className="mt-16 md:mt-20">
          <RhythmTimeline />
        </div>
        <div className="mt-16 flex flex-wrap gap-3">
          <Magnetic>
            <Button to="/kontakt" variant="mint" arrow trackId="contact_us" trackProps={{ location: "pulse_process" }}>Kontakta oss</Button>
          </Magnetic>
          <Button href="#faq" variant="outline">Läs vanliga frågor</Button>
          <Button to="/priser" variant="ghost">Priser för Fenny Pulse</Button>
        </div>
      </Section>

      <FormatShowcase />

      {/* Closing feature video */}
      <Section className="py-12 md:py-16">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <VideoEmbed id="O2zdqLfmM5E" title="Garageportexperten Hemma" />
          </Reveal>
        </div>
      </Section>

      <PulseFAQ />

      <ContactForm location="pulse" defaultIntent="pulse" />
    </div>
  );
}
